import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { Form, FormInstance, message, Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IGeneralSettings } from '../models/generalSettingsModel';
import { normFileAsDataUrl } from '../utils/fileListContainer';
import { validateFileExtension } from '../utils/fileValidator';
import { IGeneralSettingsForm } from './GeneralSettings';

const addFile = ({ onSuccess }: any) => {
  setTimeout(() => {
    onSuccess('ok');
  }, 0);
};

const StyledImage = styled.img`
  width: unset;
  height: unset;
  max-width: 128px;
  max-height: 128px;
`;

const StyledDragger = styled(Upload.Dragger)`
  display: flex;
  justify-content: center;
  padding: 1em;
`;

const supportedImageTypes = '.jpg,.png';
const validmimeTypes = ['image/png', 'image/jpeg'];

const validateImage = async (file: File, t: TFunction) => {
  let reason = `${t('imageSizeError')} ${10}MB!`;
  const validExtension = await validateFileExtension(file);
  if (!validExtension) {
    reason = `${t('wrongImageFormatError')} ${supportedImageTypes}`;
  }
  if (validmimeTypes.includes(file.type)) {
    if (file.size / 1024 / 1024 <= 10) {
      return true;
    }
  } else {
    reason = `${t('wrongImageFormatError')} ${supportedImageTypes}`;
  }
  message.warning(reason);
  throw new Error(reason);
};

export interface IImageUploadProps {
  form: FormInstance<IGeneralSettingsForm>;
  fieldName: 'municipalityIcon' | 'municipalityLogo';
}
export const ImageUpload = ({ form, fieldName }: IImageUploadProps) => {
  const [imageUrl, setImageUrl] = React.useState<string | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const { t } = useTranslation('geosecma-settings');
  const image = form.getFieldValue(fieldName);

  if (image && image.content && !imageUrl) {
    setImageUrl(image.content);
  } else if (image && image.length) {
    if (image[0].originFileObj) {
      normFileAsDataUrl(image[0].originFileObj).then((url) => setImageUrl(url));
    } else if (image[0].content && !imageUrl) {
      setImageUrl(image[0].content);
    }
  }

  const getValueFromEvent = (e: any) => {
    if (e.file.status === 'removed') {
      return [];
    }

    if (Array.isArray(e)) {
      return e;
    }

    return e && [e.file];
  };

  const handleValidateFile = async (file: RcFile) => {
    await validateImage(file, t);
  };

  const handleOnChange = async (info: any) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
    }
    if (info.file.status === 'removed') {
      form.setFields([{ name: [fieldName], value: undefined }]);
      setImageUrl(undefined);
    }
    if (info.file.status === 'done') {
      const url = await normFileAsDataUrl(info.file.originFileObj);

      setImageUrl(url);
      setLoading(false);
    }
  };

  return (
    <Form.Item
      noStyle
      shouldUpdate={(prevValues: IGeneralSettings, currentValues: IGeneralSettings) => {
        return currentValues.municipalityIcon !== prevValues.municipalityIcon;
      }}
    >
      {() => {
        return (
          <Form.Item
            label={t(fieldName)}
            name={fieldName}
            valuePropName="fileList"
            getValueFromEvent={getValueFromEvent}
            rules={[
              {
                required: true,
                message: t(`missing${fieldName}`)
              }
            ]}
          >
            <StyledDragger
              data-testid={`${fieldName}Upload`}
              customRequest={addFile}
              onChange={handleOnChange}
              accept={supportedImageTypes}
              beforeUpload={handleValidateFile}
              showUploadList={{
                showPreviewIcon: false,
                showDownloadIcon: false,
                showRemoveIcon: true
              }}
            >
              {imageUrl && !loading ? (
                <StyledImage src={imageUrl} alt={fieldName} />
              ) : (
                <>
                  <p className="ant-upload-drag-icon">{loading ? <LoadingOutlined /> : <UploadOutlined />}</p>
                  <p className="ant-upload-text">{t('upload')}</p>
                </>
              )}
            </StyledDragger>
          </Form.Item>
        );
      }}
    </Form.Item>
  );
};
